import { useState } from 'react';
import { Layers, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

/**
 * Renders an expandable chip listing the member assets of a COMBO asset
 * allocation, and flags a member that is UNAVAILABLE so dispatchers catch
 * clashes early:
 *   - out_of_service   : the member asset's status is 'out_of_service'
 *   - booked_elsewhere : the member is allocated on a DIFFERENT overlapping job
 *
 * Collapsed: "Combo · N" pill (amber + ⚠ when any member has an issue).
 * Expanded (tap): full member list, offending members highlighted amber with a tag.
 *
 * Props:
 *   equipmentId     - the allocated (combo) asset id
 *   getEquipmentInfo- (id) => equipment doc (must include combo_members + status)
 *   allocations     - full allocations list (for booked-elsewhere detection)
 *   allocation      - the CURRENT combo allocation ({job_id,start_date,end_date})
 *   variant, jobId
 */
export const ComboMembers = ({
  equipmentId,
  getEquipmentInfo,
  allocations = [],
  allocation = null,
  variant = 'list',
  jobId,
}) => {
  const [open, setOpen] = useState(false);

  const combo = equipmentId ? getEquipmentInfo(equipmentId) : null;
  const members = (combo?.combo_members || []).filter((m) => m && m.equipment_id);
  if (members.length === 0) return null;

  const labelFor = (m) => {
    const eq = getEquipmentInfo(m.equipment_id);
    return eq?.asset_id || m.asset_id || eq?.name || m.component_type_name || 'Asset';
  };

  const overlaps = (a, b) => {
    if (!a || !b) return false;
    const aS = a.start_date, aE = a.end_date || a.start_date;
    const bS = b.start_date, bE = b.end_date || b.start_date;
    if (!aS || !bS) return false;
    return aS <= bE && aE >= bS;
  };

  // Every physical asset an allocation ties up (itself + its combo members,
  // prime mover, extra trailers, legacy combo component ids).
  const occupiedIds = (a) => {
    const ids = [];
    if (a.equipment_id) {
      ids.push(a.equipment_id);
      const eq = getEquipmentInfo(a.equipment_id);
      (eq?.combo_members || []).forEach((cm) => cm?.equipment_id && ids.push(cm.equipment_id));
    }
    if (a.prime_mover_equipment_id) ids.push(a.prime_mover_equipment_id);
    (a.additional_trailer_equipment_ids || []).forEach((t) => t && ids.push(t));
    (a.combo_equipment_ids || []).forEach((c) => c && ids.push(c));
    return ids;
  };

  const issueFor = (m) => {
    const eq = getEquipmentInfo(m.equipment_id);
    if (eq?.status === 'out_of_service') return 'out_of_service';
    if (
      allocation &&
      (allocations || []).some(
        (a) =>
          a.job_id !== allocation.job_id &&
          overlaps(a, allocation) &&
          occupiedIds(a).includes(m.equipment_id)
      )
    ) {
      return 'booked_elsewhere';
    }
    return null;
  };

  const ISSUE_LABEL = { out_of_service: 'Out of service', booked_elsewhere: 'Booked elsewhere' };

  const rows = members.map((m) => ({ m, issue: issueFor(m) }));
  const hasIssue = rows.some((r) => r.issue);

  const hoverTitle = rows
    .map(({ m, issue }) => `${m.component_type_name || 'Member'}: ${labelFor(m)}${issue ? ` — ${ISSUE_LABEL[issue]}` : ''}`)
    .join('\n');

  const pillCls = hasIssue
    ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
    : 'bg-purple-500/15 text-purple-300 hover:bg-purple-500/25';

  return (
    <div className="space-y-0.5" data-testid={`combo-members-${jobId}`}>
      <button
        type="button"
        title={hoverTitle}
        data-testid={`combo-members-toggle-${jobId}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${pillCls}`}
      >
        {hasIssue ? <AlertTriangle className="h-3 w-3" /> : <Layers className="h-3 w-3" />}
        {combo?.asset_id || combo?.name || 'Combo'} · {members.length}
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {open && (
        <div className="space-y-0.5 pl-1" data-testid={`combo-members-expanded-${jobId}`}>
          {rows.map(({ m, issue }, i) => (
            <div
              key={m.equipment_id || i}
              className={`flex items-center gap-1 truncate ${issue ? 'text-amber-400' : 'text-muted-foreground'}`}
              data-testid={`combo-member-row-${jobId}-${i}`}
            >
              {issue ? (
                <AlertTriangle className="h-3 w-3 text-amber-400 shrink-0" />
              ) : (
                <Layers className="h-3 w-3 text-purple-400/70 shrink-0" />
              )}
              <span className="truncate text-[11px]">
                {m.component_type_name ? `${m.component_type_name} / ` : ''}
                {labelFor(m)}
              </span>
              {issue && (
                <span
                  className="ml-1 shrink-0 rounded bg-amber-500/20 px-1 text-[9px] font-semibold text-amber-300"
                  data-testid={`combo-member-issue-${jobId}-${i}`}
                >
                  {ISSUE_LABEL[issue]}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComboMembers;
